// Account/AdminEditUserModal.tsx

import React, { useEffect, useMemo, useState } from 'react';
import {
  Dimensions,
  Platform,
  StyleSheet,
  Pressable,
} from 'react-native';
import {
  View,
  Text,
  VStack,
  HStack,
  Input,
  CheckIcon,
  Button,
  Select as NBSelect,
} from 'native-base';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';
import { Portal } from 'react-native-paper';
import { Eye, EyeOff } from 'lucide-react-native';

import { useAppContext } from '../../../../../Universal/API/Context-API/AppContext';
import { webFont } from '../../../../../Universal/API/API';
import { DeanteUser, EditUserPayload } from './interfaces';
import PlatformSelect from '../../../../../Universal/Inputs/PlatformSelect';



interface AdminEditUserModalProps {
  isOpen: boolean;
  user: DeanteUser | null;
  onClose: () => void;
  onSave: (payload: EditUserPayload) => Promise<void> | void;
}



const { height: SCREEN_HEIGHT } = Dimensions.get('window');

const AdminEditUserModal: React.FC<AdminEditUserModalProps> = ({
  isOpen,
  user,
  onClose,
  onSave,
}) => {
  const { scaleAll, OTHER_INFO_FIELDS: OF} = useAppContext();

  const OTHER_INFO_FIELDS = React.useMemo(() => OF ?? [] , [OF] )
  
  

  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [discount, setDiscount] = useState<string>('0');
  const [role, setRole] = useState<'user' | 'admin'>('user');

  const [otherInfoValues, setOtherInfoValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const styles = useMemo(() => createStyles(scaleAll), [scaleAll]);

  useEffect(() => {
    if (user && isOpen) {
      setLogin(user.login);
      setPassword('');
      setDiscount(String(user.discount ?? 0));
      setRole((user.role as 'user' | 'admin') || 'user');

      const base: Record<string, string> = {};
      OTHER_INFO_FIELDS.forEach((field) => {
        const val =
          user.other_info &&
          typeof user.other_info === 'object' &&
          !Array.isArray(user.other_info)
            ? String(user.other_info[field.key] ?? '')
            : '';
        base[field.key] = val;
      });

      setOtherInfoValues(base);
      setError(null);
    }
  }, [user, isOpen]);

  const handleChangeOtherInfo = (key: string, value: string) => {
    setOtherInfoValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = async () => {
    if (!user) return;

    setError(null);
    setSaving(true);

    try {
      const parsedDiscount = parseInt(discount, 10);
      const safeDiscount = Number.isNaN(parsedDiscount) ? 0 : parsedDiscount;

      const otherInfoObj: Record<string, string> = {};
      OTHER_INFO_FIELDS.forEach((field) => {
        const val = (otherInfoValues[field.key] || '').trim();
        if (val !== '') {
          otherInfoObj[field.key] = val;
        }
      });

      const payload: EditUserPayload = {
        target_login: user.login,
        role,
        discount: safeDiscount,
        other_info: otherInfoObj, // даже если {}, можно так очищать
      };

      if (login.trim() && login.trim() !== user.login) {
        payload.login = login.trim();
      }

      if (password.trim()) {
        payload.password = password.trim();
      }

      await onSave(payload);
      setSaving(false);
      onClose();
    } catch (e) {
      console.error('Error in AdminEditUserModal.handleSave', e);
      setError('Не удалось сохранить изменения');
      setSaving(false);
    }
  };

  if (!isOpen || !user) return null;

  return (
    <Portal>
      <View style={{ height: SCREEN_HEIGHT, width: '100%' }}>
        <Animated.View
          entering={FadeIn.duration(200)}
          exiting={FadeOut.duration(200)}
          style={[StyleSheet.absoluteFill, styles.backdropRoot]}
        >
          {/* кликабельный бэкдроп */}
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={saving ? undefined : onClose}
          />

          {/* сама модалка по центру */}
          <View style={styles.centerWrapper} pointerEvents="box-none">
            <View style={styles.modalCard}>
              {/* хедер */}
              <HStack justifyContent="space-between" alignItems="center" mb={3}>
                <Text style={styles.modalTitle}>Редактирование пользователя</Text>
                <Button
                  variant="ghost"
                  size="xs"
                  onPress={saving ? () => {} : onClose}
                  _text={{ fontSize: 14 * scaleAll }}
                >
                  ✕
                </Button>
              </HStack>

              {/* контент */}
              <VStack space={3}>
                <Input
                  placeholder="Логин"
                  value={login}
                  onChangeText={setLogin}
                  autoCapitalize="none"
                />

                <Input
                  placeholder="Новый пароль (опционально)"
                  value={password}
                  onChangeText={setPassword}
                  type={showPassword ? 'text' : 'password'}
                  InputRightElement={
                    <Pressable onPress={() => setShowPassword((prev) => !prev)}>
                      {showPassword ? (
                        <EyeOff
                          size={20 * scaleAll}
                          color="#6A6A6A"
                          style={{ marginRight: 12 }}
                        />
                      ) : (
                        <Eye
                          size={20 * scaleAll}
                          color="#6A6A6A"
                          style={{ marginRight: 12 }}
                        />
                      )}
                    </Pressable>
                  }
                />

                <HStack space={3}>
                  <Input
                    flex={1}
                    placeholder="Скидка, %"
                    keyboardType="numeric"
                    value={discount}
                    onChangeText={setDiscount}
                  />

                  <PlatformSelect
                    flex={1}
                    selectedValue={role}
                    minWidth={150}
                    accessibilityLabel="Роль"
                    placeholder="Роль"
                    _selectedItem={{
                      endIcon: <CheckIcon size={4} />,
                    }}
                    onValueChange={(itemValue) =>
                      setRole(itemValue as 'user' | 'admin')
                    }
                  >
                    <NBSelect.Item label="User" value="user" />
                    <NBSelect.Item label="Admin" value="admin" />
                  </PlatformSelect>
                </HStack>

                <VStack space={2} mt={2}>
                  <Text style={styles.sectionTitle}>Дополнительная информация</Text>

                  {OTHER_INFO_FIELDS.map((field) => (
                    <Input
                      key={field.key}
                      placeholder={field.placeholder ?? field.label}
                      value={otherInfoValues[field.key] ?? ''}
                      onChangeText={(text) => handleChangeOtherInfo(field.key, text)}
                    />
                  ))}
                </VStack>

                {error && (
                  <Text style={styles.errorText}>
                    {error}
                  </Text>
                )}
              </VStack>

              {/* футер */}
              <HStack justifyContent="flex-end" space={2} mt={4}>
                <Button
                  variant="ghost"
                  onPress={onClose}
                  isDisabled={saving}
                >
                  Отмена
                </Button>
                <Button
                  onPress={handleSave}
                  isLoading={saving}
                >
                  Сохранить
                </Button>
              </HStack>
            </View>
          </View>
        </Animated.View>
      </View>
    </Portal>
  );
};

const createStyles = (scaleAll: number) =>
  StyleSheet.create({
    backdropRoot: {
      zIndex: 9999,
      backgroundColor: 'rgba(0,0,0,0.75)',
    },
    centerWrapper: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: 16 * scaleAll,
    },
    modalCard: {
      width: '100%',
      maxWidth: 420,
      maxHeight: SCREEN_HEIGHT - 80,
      backgroundColor: 'white',
      borderRadius: 12 * scaleAll,
      paddingHorizontal: 16 * scaleAll,
      paddingVertical: 16 * scaleAll,
    },
    modalTitle: {
      fontFamily: webFont('Inter'),
      fontSize: 16 * scaleAll,
      fontWeight: '600',
      color: '#111827',
    },
    sectionTitle: {
      fontFamily: webFont('Inter'),
      fontSize: 13 * scaleAll,
      fontWeight: '500',
      color: '#4B5563',
    },
    errorText: {
      marginTop: 4 * scaleAll,
      fontFamily: webFont('Inter'),
      fontSize: 12 * scaleAll,
      color: '#DC2626',
    },
  });

export default AdminEditUserModal;
